import get from 'lodash/get'
import { extent, min } from 'd3-array'
import { select, event as d3event } from 'd3-selection'
import { scaleLinear } from 'd3-scale'
import { neighborhoodDesk, primarySeat, seatable, sharedDesk } from './utils'
import getContours from './getContours'
import { groupDepartmentCluster, convertDepartmentSpacesToPoints } from './departmentAreas'
import { convertNeighborhoodSpacesToPoints, groupNeighborhoodCluster } from './neighborhoodAreas'
import {
  convertFacilityGroupsSpacesToPoints,
  groupFacilityGroupCluster,
} from './facilityGroupsAreas'
import MapImage from './MapImage'
import Block from './Block'
import BlockHighlight from './BlockHighlight'
import Seatable from './Seatable'
import MeetingRoom from './MeetingRoom'
import Pin from './Pin'
import Device from './Device'
import Area from './Area'
import Zoom from './Zoom'
import Tooltip from './Tooltip'
import Selection from './Selection'
import EmployeeDrag from './EmployeeDrag'
import MapObjectDrag from './MapObjectDrag'
import findMapObjectByCoordinates from './findMapObjectByCoordinates'
import findNeighborhoodByCoordinates from './findNeighborhoodByCoordinates'
import getFontSize from './getFontSize'
import getFontConfig from './getFontConfig'
import getSpaceRadius from './getSpaceRadius'
import fontObserver from './FontObserver'
import { convertTeamSpacesToPoints, groupTeamCluster } from './teamAreas'
import YouAreHere from './YouAreHere'
import inView from './inView'
import drawRect from './drawRect'
import deviceResolution from './deviceResolution'
import {
  hasDeptManageRights,
  hasLocationManageRights,
  isAdmin,
  isDepartmentAdmin,
  isLocationAdmin,
  isPrimary,
  isShared,
  isSpaceCaptain,
} from '@utils'
import UnseatZone from 'components/AdminFloorPlan/Cards/UnassignEmployeeDragArea/UnseatZone'
import {
  convertEmployeesWorkplaceGroupsSpacesToPoints,
  convertSpacesWorkplaceGroupsSpacesToPoints,
  groupWorkplaceGroupCluster,
  groupEmpWorkplaceGroupCluster,
} from './workplaceGroupAreas'
import { toRadians, rotate } from './applyRotation'
import space from '@api/space'

export const MIN_SCALE = 0.8
export const MAX_SCALE = 20

class Map {
  constructor({
    print,
    interactive,
    enableTooltip,
    enableSelection,
    enableMultiSelection,
    enableZoomWidget,
    enableEmployeeDrag,
    enableMapObjectDrag,
    touchScreen,
    liveModeOn,
    user,
    screen,
    map,
    pixelsPerFoot,
    assetVersion,
    blockInstances,
    spaces,
    pins,
    devices,
    selectedIds = {},
    filteredIds = {},
    selectedNeighborhoodId,
    selectedDepartmentId,
    container,
    showDepartmentsArea,
    showFacilityGroupsArea,
    editingBlocks,
    showNeighborhoodsArea,
    showWorkplaceGroupsEmployeesArea,
    showWorkplaceGroupsSpacesArea,
    showTeamsArea,
    toolbarOffset,
    floor,
    force,
    mapViewSettings,
    assetFilterSettings,
    youAreHere,
    subscriptions,
    rotation,
    ...actions
  }) {
    this.RESOLUTION = print ? 3 : deviceResolution()
    this.toolbarOffset = toolbarOffset
    this.enableTooltip = enableTooltip
    this.enableSelection = enableSelection
    this.enableMultiSelection = enableMultiSelection
    this.enableZoomWidget = enableZoomWidget
    this.enableEmployeeDrag = enableEmployeeDrag
    this.enableMapObjectDrag = enableMapObjectDrag
    this.interactive = interactive
    this.touchScreen = touchScreen
    this.liveModeOn = liveModeOn
    this.print = print
    this.user = user
    this.screen = screen
    this.map = map
    this.assetVersion = assetVersion
    this.blockInstances = blockInstances
    this.spaces = spaces
    this.pins = pins
    this.devices = devices
    this.actions = actions
    this.filteredIds = filteredIds
    this.force = force
    this.mapViewSettings = mapViewSettings
    this.assetFilterSettings = assetFilterSettings
    this.print = print
    this.floorId = floor.id
    if (youAreHere) {
      this.youAreHere = {
        ...youAreHere,
        ...rotate({
          angle: toRadians(rotation),
          center: { x: map.w / 2, y: map.h / 2 },
          point: youAreHere,
        }),
      }
    }
    this.pixelsPerFoot = pixelsPerFoot
    this.minScale = MIN_SCALE
    this.subscriptions = subscriptions || {}
    this.maxScale = MAX_SCALE
    this.floor = floor

    this.selectedIds = selectedIds
    this.selectedNeighborhoodId = selectedNeighborhoodId
    this.selectedDepartmentId = selectedDepartmentId
    this.showDepartmentsArea = showDepartmentsArea
    this.showFacilityGroupsArea = showFacilityGroupsArea
    this.editingBlocks = editingBlocks
    this.showNeighborhoodsArea = showNeighborhoodsArea
    this.showTeamsArea = showTeamsArea
    this.showWorkplaceGroupsEmployeesArea = showWorkplaceGroupsEmployeesArea
    this.showWorkplaceGroupsSpacesArea = showWorkplaceGroupsSpacesArea

    this.container = select(container).classed('canvasMapContainer', true)
    this.dragPreviewContainer = this.container.append('div').node()

    if (enableEmployeeDrag) {
      this.unseatZoneContainer = this.container.append('div').node()
    }
    if (enableZoomWidget) {
      this.zoomWidgetContainer = this.container.append('div').node()
    }

    this.canvas = select(container)
      .insert('canvas', ':first-child')
      .style('position', 'absolute')
      .style('top', 0)
      .style('left', 0)
      .style('z-index', 1)
      .style('pointer-events', 'none')
      .node()

    this.imageContainer = select(container)
      .insert('div', ':first-child')
      .attr('id', 'floorImageContainer')
      .style('position', 'relative')
      .style('overflow', 'hidden')

    this.context = this.canvas.getContext('2d')
    this.transform = { x: 0, y: 0, k: 1 }

    this.updateCanvasSize()
    this.updateScales()
    this.updateOffsets()
    this.initMapImage()
    this.initBlocks()
    this.calculateAreas()

    if (enableTooltip) {
      this.tooltip = new Tooltip(this)
    }

    this.zoom = new Zoom(this)

    if (enableEmployeeDrag) {
      this.unseatZone = new UnseatZone(this)
      this.drag = new EmployeeDrag(this)
    } else if (enableMapObjectDrag) {
      this.drag = new MapObjectDrag(this)
    }

    if (enableSelection) {
      this.selection = new Selection(this)
    }

    fontObserver(() => {
      this.iconFontLoaded = true
      this.draw()
      window.iconFontLoaded = true
    })
  }

  // Call this method to update map in componentWillReceiveProps
  update = ({
    liveModeOn,
    screen,
    map,
    assetVersion,
    pixelsPerFoot,
    blockInstances,
    spaces,
    pins,
    devices,
    selectedIds,
    filteredIds = {},
    selectedNeighborhoodId,
    selectedDepartmentId,
    showDepartmentsArea,
    showFacilityGroupsArea,
    editingBlocks,
    showNeighborhoodsArea,
    showWorkplaceGroupsEmployeesArea,
    showWorkplaceGroupsSpacesArea,
    showTeamsArea,
    renumbering,
    nextSpaceCode,
    selectedItem,
    itemW,
    itemH,
    force,
    floor,
    updateYouAreHere,
    youAreHere,
    mapViewSettings,
    assetFilterSettings,
    isDirectBookingFlow,
    ...actions
  }) => {
    this.liveModeOn = liveModeOn
    this.screen = screen
    this.map = map
    this.assetVersion = assetVersion
    this.blockInstances = blockInstances
    this.spaces = spaces
    this.pins = pins
    this.devices = devices
    this.actions = { ...this.actions, ...actions }
    this.filteredIds = filteredIds
    this.selectedIds = selectedIds
    this.selectedNeighborhoodId = selectedNeighborhoodId
    this.selectedDepartmentId = selectedDepartmentId
    this.renumbering = renumbering
    this.nextSpaceCode = nextSpaceCode
    this.selectedItem = selectedItem
    this.itemW = itemW
    this.itemH = itemH
    this.force = force
    this.floor = floor
    this.mapViewSettings = mapViewSettings
    this.assetFilterSettings = assetFilterSettings
    this.isDirectBookingFlow = isDirectBookingFlow
    if (updateYouAreHere) {
      this.youAreHere = youAreHere
    }
    this.showDepartmentsArea = showDepartmentsArea
    this.showFacilityGroupsArea = showFacilityGroupsArea
    this.showTeamsArea = showTeamsArea
    this.showNeighborhoodsArea = showNeighborhoodsArea
    this.showWorkplaceGroupsEmployeesArea = showWorkplaceGroupsEmployeesArea
    this.showWorkplaceGroupsSpacesArea = showWorkplaceGroupsSpacesArea
    this.editingBlocks = editingBlocks
    this.pixelsPerFoot = pixelsPerFoot
    this.maxScale = MAX_SCALE

    this.tooltip && this.tooltip.update(this)
    this.drag && this.drag.update(this)
    this.zoom && this.zoom.update(this)

    // TODO: move above module updates
    this.mapImage.update(this)
    this.updateCanvasSize()
    this.updateScales()
    this.selection.update(this)
    this.calculateAreas()
    this.handleSubscriptions('selection')
    this.draw()
  }

  updateMeetingRoomStatuses(statuses) {
    this.meetingRoomStatuses = statuses
    this.draw()
  }

  // Call this method before unmounting React component
  destroy = () => {
    this.selection && this.selection.destroy()
    // setting canvas size to zero clears memory usage in chrome and inproves performace
    this.canvas.width = 0
    this.canvas.height = 0
    this.canvas.style.width = '0px'
    this.canvas.style.height = '0px'
  }

  updateCanvasSize = () => {
    const { screen, canvas, context, RESOLUTION } = this
    canvas.width = screen.w * RESOLUTION
    canvas.height = screen.h * RESOLUTION
    canvas.style.width = `${screen.w}px`
    canvas.style.height = `${screen.h}px`
    context.scale(RESOLUTION, RESOLUTION)
  }

  updateScales = () => {
    const { minScale, maxScale, screen, map, transform: t } = this
    const domain = [0, Math.max(map.w, map.h)]
    let range = [0, Math.min(screen.w, screen.h)]

    const xScale = scaleLinear().domain(domain).range(range)
    const yScale = scaleLinear().domain(domain).range(range)
    const rScale = scaleLinear().domain(domain).range(range)
    const imageScale = scaleLinear().domain(domain)

    this.zoomScale = scaleLinear()
      .domain([minScale, maxScale])
      .range([minScale, maxScale / 4])

    this.opacityScale = scaleLinear()
      .domain([minScale, maxScale / (0.35 * maxScale)])
      .range([1, 0.5])
      .clamp(true)

    let width = xScale(map.w)
    let height = yScale(map.h)
    let zoomLevel = 1

    const zoom1 = screen.w / width
    const zoom2 = screen.h / height

    zoomLevel = Math.min(zoom1, zoom2)
    width *= zoomLevel
    height *= zoomLevel

    range = [0, Math.max(width, height)]
    xScale.range(range)
    yScale.range(range)
    this.rScale = rScale.range(range)
    this.imageScale = imageScale.range(range)

    if (this.xScale && t.rescaleX && t.rescaleY) {
      this.xScale = t.rescaleX(xScale)
      this.yScale = t.rescaleY(yScale)
    } else {
      this.xScale = xScale
      this.yScale = yScale
    }
  }

  initMapImage = () => {
    this.mapImage = new MapImage(this)
    this.mapImage
      .onLoad()
      .catch(() => {
        window.mapImageError = true
      })
      .then(() => {
        window.mapImageLoaded = true
      })
  }

  initBlocks = () => {
    const {
      assetVersion: { blocks },
    } = this
    let blocksToLoad = blocks.length
    this.blockImages = {}

    blocks.forEach(({ id, url }) => {
      const image = new Image()
      image.onload = () => {
        this.blockImages[id] = image
        blocksToLoad--
        if (blocksToLoad === 0) {
          this.draw()
        }
      }

      image.src = url
    })
  }

  subscribe = (action, callback) => {
    this.subscriptions[action] = this.subscriptions[action] || []
    if (this.subscriptions[action].indexOf(callback) === -1)
      this.subscriptions[action].push(callback)
  }

  unsubscribe = (action, callback) => {
    const index = (this.subscriptions[action] || []).indexOf(callback)
    if (index !== -1) this.subscriptions[action].splice(index, 1)
  }

  handleSubscriptions = (action, args) => {
    const callbacks = this.subscriptions[action] || []
    callbacks.forEach((cb) => cb(args))
  }

  onZoom = () => {
    this.transform = d3event.transform
    this.updateScales()
    this.tooltip && this.tooltip.update(this)
    this.zoom && this.zoom.update(this)
    this.selection && this.selection.update(this)
    this.drag && this.drag.update(this)
    this.draw()
    this.actions.onZoom && this.actions.onZoom()
    this.handleSubscriptions('onZoom')
  }

  onZoomEnd = () => {
    this.actions.onZoomEnd && this.actions.onZoomEnd()
    this.handleSubscriptions('onZoomEnd')
  }

  updateOffsets = () => {
    const { screen, map, xScale, yScale } = this
    this.dx = (screen.w - xScale(map.w)) / 2
    this.dy = (screen.h - yScale(map.h)) / 2
  }

  updateDraggedItemAndTarget = (draggedItem, toSpace, nh) => {
    let changed = false
    if (draggedItem !== this.draggedItem) {
      this.draggedItem = draggedItem
      changed = true
    }

    if (toSpace !== this.toSpace) {
      this.toSpace = toSpace
      changed = true
    }

    if (nh !== this.nh) {
      this.nh = nh
      this.toSpace = null
      changed = true
    }

    if (changed) {
      this.draw()
    }
  }

  handleOutsideDrag = ({ x, y }, employee) => {
    let toSpace = findMapObjectByCoordinates([x, y - 96], this)
    let nh
    if (
      (!toSpace || get(toSpace, 'spaceAssignment.neighborhood')) &&
      this.showNeighborhoodsArea &&
      this.areas
    ) {
      nh = findNeighborhoodByCoordinates([x, y - 98], this)
      // ignore space if dragging over neighborhood
      if (nh) {
        toSpace = null
      }
    }

    this.updateDraggedItemAndTarget(employee, toSpace, nh)
  }

  handleOutsideDrop = ({ x, y }, employee) => {
    const {
      toSpace,
      nh,
      actions: { seatEmployee, dragEmployeeToNeighborhoodFromSearch },
    } = this
    const fromSpaceId = get(employee, 'seat.id')

    if (
      toSpace &&
      seatable(toSpace.spaceType) &&
      (isPrimary(toSpace.usageType) || isShared(toSpace.usageType)) &&
      toSpace.id !== fromSpaceId
    ) {
      seatEmployee(employee, toSpace)
    }
    if (nh || get(toSpace, 'spaceAssignment.neighborhood')) {
      dragEmployeeToNeighborhoodFromSearch(nh, toSpace, employee)
    }

    this.updateDraggedItemAndTarget(null, null)
  }

  zoomTo = ({ x, y, k, animate = true }) => {
    const zoomLevel =
      k === 0 // auto calculate zoom level when zoom param === 0
        ? Math.max(0.25 * this.maxScale, 1.5)
        : Math.min(Math.max(this.minScale, k) || 0.25 * this.maxScale, this.maxScale)
    this.zoom.zoomTo({ animate, x, y, k: zoomLevel })
  }

  zoomToArea = ({ areaId, animate = true }) => {
    const { screen, areas, imageScale } = this
    const _areas = (areas || []).filter((a) => a.id === areaId)

    if (_areas.length) {
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      let cx = 0
      let cy = 0

      _areas.forEach((a) => {
        const [_minX, _maxX] = extent(a, (p) => p[0])
        const [_minY, _maxY] = extent(a, (p) => p[1])
        if (_minX < minX) minX = _minX
        if (_maxX > maxX) maxX = _maxX
        if (_minY < minY) minY = _minY
        if (_maxY > maxY) maxY = _maxY
        cx += a.center.x
        cy += a.center.y
      })

      cx /= _areas.length
      cy /= _areas.length

      const w = imageScale(maxX - minX)
      const h = imageScale(maxY - minY)

      const k = Math.min(0.9 * (Math.min(screen.w, screen.h) / Math.max(w, h)), 2)

      this.zoom.zoomTo({
        x: cx,
        y: cy,
        k,
        animate,
      })
    }
  }

  updatePreview = ({ x, y }) => {
    this.previewLocation = { x, y }
    this.draw()
  }

  createMapObject = () => {
    const { previewLocation, selectedItem } = this

    if (previewLocation && selectedItem) {
      const params = {
        x: previewLocation.x,
        y: previewLocation.y,
      }
      if (selectedItem.device) {
        this.actions.createDevice(params)
      } else if (selectedItem.icon) {
        this.actions.createPin(params)
      } else if (selectedItem.url) {
        this.actions.createBlockInstance(params)
      } else {
        this.actions.createSpace(params)
      }
    }
  }

  getSpaceConfig = (space) => {
    const { dx, dy, transform: t, rScale, xScale, yScale, screen } = this
    const spaceConfig = {
      x: dx * t.k + xScale(space.x),
      y: dy * t.k + yScale(space.y),
      r: getSpaceRadius({ space, k: t.k, rScale: rScale }),
    }

    return { spaceConfig, inView: inView({ ...spaceConfig, screen }) }
  }

  getSelectionRect = () => {
    const { dx, dy, transform, xScale, yScale, rScale, maxScale, selectedIds, spaces } = this

    const ids = Object.keys(selectedIds)
    if (!ids.length) return null
    if (ids.length === 1 && !spaces.byId[ids[0]]) return null

    let minX = Infinity
    let maxX = 0
    let minY = Infinity
    let maxY = 0
    let maxW = 0

    ids.forEach((spaceId) => {
      const space = spaces.byId[spaceId]
      if (space) {
        minX = Math.min(minX, space.x)
        maxX = Math.max(maxX, space.x)
        minY = Math.min(minY, space.y)
        maxY = Math.max(maxY, space.y)
        maxW = Math.max(maxW, space.w)
      }
    })

    const r =
      1.5 *
      getSpaceRadius({
        space: { w: maxW },
        k: transform.k,
        rScale,
        maxScale,
      })
    minX = dx * transform.k + xScale(minX)
    maxX = dx * transform.k + xScale(maxX)
    minY = dy * transform.k + yScale(minY)
    maxY = dy * transform.k + yScale(maxY)

    return {
      x: minX - r,
      y: minY - r,
      w: maxX - minX + 2 * r,
      h: maxY - minY + 2 * r,
    }
  }

  calculateAreas = () => {
    this.areas = []

    if (this.showDepartmentsArea) {
      this.areas = getContours({
        points: convertDepartmentSpacesToPoints(this.spaces),
        context: this.context,
        scale: this.rScale,
        fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
        pixelsPerFoot: this.pixelsPerFoot,
        groupCluster: groupDepartmentCluster,
        getGroupColor: (group) => get(group, '[0].department.color'),
        getGroupName: (group) => get(group, '[0].department.name'),
        getGroupId: (group) => get(group, '[0].department.id'),
      })
    } else if (this.selectedDepartmentId) {
      let ids = []
      let byId = {}

      this.spaces.ids.forEach((id) => {
        const space = this.spaces.byId[id]

        if (get(space, 'spaceAssignment.department.id') === this.selectedDepartmentId) {
          ids = [...ids, id]
          byId = { ...byId, [id]: space }
        }
      })

      this.areas = this.areas.concat(
        getContours({
          points: convertDepartmentSpacesToPoints(this.spaces),
          context: this.context,
          scale: this.rScale,
          fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
          pixelsPerFoot: this.pixelsPerFoot,
          groupCluster: groupDepartmentCluster,
          getGroupColor: (group) => get(group, '[0].department.color'),
          getGroupName: (group) => get(group, '[0].department.name'),
          getGroupId: (group) => get(group, '[0].department.id'),
        })
      )
    }

    if (this.showFacilityGroupsArea) {
      this.areas = this.areas.concat(
        getContours({
          points: convertFacilityGroupsSpacesToPoints(this.spaces),
          context: this.context,
          scale: this.rScale,
          fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
          pixelsPerFoot: this.pixelsPerFoot,
          groupCluster: groupFacilityGroupCluster,
          getGroupColor: (group) => get(group, '[0].facilityGroup.color'),
          getGroupName: (group) => get(group, '[0].facilityGroup.name'),
          getGroupId: (group) => get(group, '[0].facilityGroup.id'),
        })
      )
    }

    if (this.showNeighborhoodsArea) {
      this.areas = this.areas.concat(
        getContours({
          points: convertNeighborhoodSpacesToPoints(this.spaces),
          context: this.context,
          scale: this.rScale,
          fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
          pixelsPerFoot: this.pixelsPerFoot,
          groupCluster: groupNeighborhoodCluster,
          getGroupColor: (group) => get(group, '[0].neighborhood.color'),
          getGroupName: (group) => get(group, '[0].neighborhood.name'),
          getGroupId: (group) => get(group, '[0].neighborhood.id'),
          areaType: 'neighborhood',
        })
      )
    } else if (this.selectedNeighborhoodId) {
      let ids = []
      let byId = {}

      this.spaces.ids.forEach((id) => {
        const space = this.spaces.byId[id]
        if (get(space, 'spaceAssignment.neighborhood.id') === this.selectedNeighborhoodId) {
          ids = [...ids, id]
          byId = { ...byId, [id]: space }
        }
      })

      this.areas = this.areas.concat(
        getContours({
          points: convertNeighborhoodSpacesToPoints({ ids, byId }),
          context: this.context,
          scale: this.rScale,
          fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
          pixelsPerFoot: this.pixelsPerFoot,
          groupCluster: groupNeighborhoodCluster,
          getGroupColor: (group) => get(group, '[0].neighborhood.color'),
          getGroupName: (group) => get(group, '[0].neighborhood.name'),
          getGroupId: (group) => get(group, '[0].neighborhood.id'),
          areaType: 'neighborhood',
        })
      )
    }

    this.areas = this.areas.concat(
      this.showTeamsArea
        ? getContours({
            points: convertTeamSpacesToPoints(this.spaces),
            context: this.context,
            scale: this.rScale,
            fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
            pixelsPerFoot: this.pixelsPerFoot,
            groupCluster: groupTeamCluster,
            getGroupColor: (group) => get(group, '[0].team.color'),
            getGroupName: (group) => get(group, '[0].team.name'),
            getGroupId: (group) => get(group, '[0].team.id'),
          })
        : []
    )

    this.areas = this.areas.concat(
      this.showWorkplaceGroupsEmployeesArea
        ? getContours({
            points: convertEmployeesWorkplaceGroupsSpacesToPoints(this.spaces),
            context: this.context,
            scale: this.rScale,
            fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
            pixelsPerFoot: this.pixelsPerFoot,
            groupCluster: groupEmpWorkplaceGroupCluster,
            getGroupColor: (group) => get(group, '[0].workplaceGroup.color'),
            getGroupName: (group) => get(group, '[0].workplaceGroup.name'),
            getGroupId: (group) => get(group, '[0].workplaceGroup.id'),
          })
        : []
    )

    this.areas = this.areas.concat(
      this.showWorkplaceGroupsSpacesArea
        ? getContours({
            points: convertSpacesWorkplaceGroupsSpacesToPoints(this.spaces),
            context: this.context,
            scale: this.rScale,
            fontSize: getFontSize(this.rScale.range()[1], this.transform.k),
            pixelsPerFoot: this.pixelsPerFoot,
            groupCluster: groupWorkplaceGroupCluster,
            getGroupColor: (group) => get(group, '[0].workplaceGroups[0].color'),
            getGroupName: (group) => get(group, '[0].workplaceGroups[0].name'),
            getGroupId: (group) => get(group, '[0].workplaceGroups[0].id'),
          })
        : []
    )
  }

  highlightNeighborhood = (nh) => {
    if (this.nh !== nh) {
      this.nh = nh
      this.draw()
    }
  }

  highlightBlockInstance = (bi) => {
    if (this.bi !== bi) {
      this.bi = bi
      this.draw()
    }
  }

  updateResizeRotateHandles = (handles) => {
    this.handles = handles
  }

  spaceInView = (space) => {
    const { x, y } = this.getSpaceConfig(space)
    return inView({ x, y, screen: this.screen })
  }

  isFiltered = (space) => {
    let filtered = false

    if (this.assetFilterSettings.length > 0) {
      const tagIds = space.tags.map((item) => item.id)
      if (tagIds.length > 0) {
        const hasCommonElement = tagIds.some((value) => this.assetFilterSettings.includes(value))

        if (hasCommonElement) {
          // Mark the space as available
          filtered = false
        } else {
          // Mark the space as not available
          filtered = true
        }
      } else {
        // If space has no tags, consider it as not available
        filtered = true
      }
    } else {
      filtered = false // Set filtered to false if space has no assetFilter
    }
    return filtered
  }

  draw() {
    const {
      liveModeOn,
      user = { role: 'admin' },
      blockInstances,
      spaces,
      pins,
      devices,
      screen,
      transform,
      minScale,
      maxScale,
      xScale,
      yScale,
      rScale,
      imageScale,
      opacityScale,
      zoomScale,
      context,
      dx,
      dy,
      toSpace,
      selectedIds,
      selectedNeighborhoodId,
      selectedDepartmentId,
      filteredIds,
      showDepartmentsArea,
      showFacilityGroupsArea,
      showNeighborhoodsArea,
      showWorkplaceGroupsEmployeesArea,
      showWorkplaceGroupsSpacesArea,
      showTeamsArea,
      draggedItem,
      areas = [],
      selectedItem,
      nextSpaceCode,
      previewLocation,
      itemW,
      itemH,
      iconFontLoaded,
      force,
      youAreHere,
      pixelsPerFoot,
      editingBlocks,
      blockImages,
      print,
      meetingRoomStatuses,
      mapViewSettings,
      assetFilterSettings,
      isDirectBookingFlow,
    } = this
    const dr = zoomScale(transform.k)
    const admin = isAdmin(user.role)
    const deptAdmin = isDepartmentAdmin(user.role)
    const locationAdmin = isLocationAdmin(user.role)
    const spaceCaptain = isSpaceCaptain(user.role)
    const fontSize = getFontSize(rScale.range()[1], transform.k)
    const smallestR = min(
      spaces.ids.map((id) => spaces.byId[id]),
      ({ w }) => w
    )
    const fontConfig = getFontConfig(rScale(smallestR) * transform.k, mapViewSettings)
    const draggedItemSpaceId = get(this.draggedItem, 'seat.id', get(this.draggedItem, 'id'))

    context.fillStyle = 'white'
    context.clearRect(0, 0, screen.w, screen.h)
    this.mapImage.draw({
      dx,
      dy,
      screen,
      opacityScale,
      imageScale,
      transform,
    })

    context.globalAlpha = opacityScale(transform.k)

    blockInstances.ids.forEach((id) => {
      const blockInstance = blockInstances.byId[id]
      const block = blockImages[blockInstance.block.id]

      block &&
        Block({
          context,
          block,
          blockInstance,
          dx,
          dy,
          imageScale,
          xScale,
          yScale,
          transform,
          screen,
        })
    })

    context.globalAlpha = 1

    if (editingBlocks) {
      const showResizeAndRotateHandles = Object.keys(selectedIds).length === 1
      if (this.bi && !selectedIds[this.bi.id]) {
        BlockHighlight({
          context,
          screen,
          bi: this.bi,
          dx,
          dy,
          transform,
          imageScale,
          xScale,
          yScale,
          showResizeAndRotateHandles,
          highlighted: true,
        })
      }

      Object.keys(selectedIds).forEach((id) => {
        const bi = blockInstances.byId[id]
        if (bi) {
          BlockHighlight({
            context,
            screen,
            bi,
            dx,
            dy,
            transform,
            imageScale,
            xScale,
            yScale,
            showResizeAndRotateHandles,
            selected: true,
            updateResizeRotateHandles: this.updateResizeRotateHandles,
          })
        }
      })
    }

    if (iconFontLoaded) {
      devices.ids.forEach((id) => {
        const device = devices.byId[id]
        const selected = selectedIds[id]

        Device({
          device,
          dx,
          dy,
          maxScale,
          rScale,
          xScale,
          yScale,
          transform,
          context,
          screen,
          selected,
          fontSize,
        })
      })

      pins.ids.forEach((id) => {
        const pin = pins.byId[id]
        const selected = selectedIds[id]
        const filtered = filteredIds[id]

        // Skip special pins, they will only be rendered in edit mode
        if (!this.enableMapObjectDrag && pin.globalMapObject.limitOnePerFloor) {
          return
        }

        Pin({
          pin,
          dx,
          dy,
          maxScale,
          rScale,
          xScale,
          yScale,
          transform,
          context,
          screen,
          filtered,
          selected,
          fontConfig,
          draggedItem,
        })
      })
    }

    spaces.ids.forEach((id, i) => {
      const space = spaces.byId[id]
      const selected = selectedIds[id]
      const filtered = this.isFiltered(space)

      if (youAreHere && id === youAreHere.id) {
        return
      }
      const neighborhoodId =
        space &&
        space.spaceAssignment &&
        space.spaceAssignment.neighborhood &&
        space.spaceAssignment.neighborhood.id

      if (isDirectBookingFlow || seatable(space.spaceType)) {
        const canDrag =
          ((primarySeat(space.usageType) || sharedDesk(space.usageType)) &&
            (admin ||
              (deptAdmin && hasDeptManageRights(user, space)) ||
              (locationAdmin && hasLocationManageRights(user, this.floorId)) ||
              (spaceCaptain &&
                hasLocationManageRights(user, this.floorId) &&
                hasDeptManageRights(user, space)))) ||
          (neighborhoodDesk(space.usageType) &&
            neighborhoodId &&
            (admin || (locationAdmin && hasLocationManageRights(user, this.floorId))))

        if (isDirectBookingFlow || seatable(space.spaceType)) {
          Seatable({
            context,
            space,
            dx,
            dy,
            maxScale,
            xScale,
            yScale,
            rScale,
            transform,
            selected,
            filtered,
            screen,
            fontSize,
            fontConfig,
            draggedItem,
            canDrag: canDrag && space.id !== draggedItemSpaceId,
            isOver:
              canDrag && space.id !== draggedItemSpaceId && space.id === (toSpace && toSpace.id),
            print,
            liveModeOn,
            isDirectBookingFlow: this.floor.isBookDeskPage || this.floor.isBookMeetingRoomPa,
            mapViewSettings,
            assetFilterSettings,
            isDirectBookingFlow,
          })
        }
      } else {
        MeetingRoom({
          context,
          space,
          dx,
          dy,
          minScale,
          maxScale,
          xScale,
          yScale,
          rScale,
          transform,
          selected,
          filtered,
          screen,
          fontConfig,
          draggedItem,
          mapViewSettings,
          assetFilterSettings,
          print,
          status: liveModeOn ? space.status : get(meetingRoomStatuses, id, 'available'),
        })
      }
    })

    if (
      showDepartmentsArea ||
      showFacilityGroupsArea ||
      showNeighborhoodsArea ||
      showTeamsArea ||
      showWorkplaceGroupsEmployeesArea ||
      showWorkplaceGroupsSpacesArea ||
      selectedNeighborhoodId ||
      selectedDepartmentId
    ) {
      areas?.forEach((area) => {
        Area({
          area,
          dx,
          dy,
          dr,
          minScale,
          maxScale,
          rScale,
          xScale,
          yScale,
          fontSize,
          transform,
          context,
          screen,
          pixelsPerFoot,
          filtered: filteredIds[area?.id] === false,
          highlighted: get(this.nh, 'id') === area?.id,
          selected: selectedNeighborhoodId || selectedDepartmentId === area?.id,
        })
      })
    }

    if (youAreHere) {
      YouAreHere({
        space: youAreHere,
        dx,
        dy,
        xScale,
        yScale,
        rScale,
        maxScale,
        transform,
        context,
        screen,
        selected: selectedIds[youAreHere.id],
      })
    }

    if (selectedItem && previewLocation) {
      if (selectedItem.url) {
        Block({
          context,
          block: blockImages[selectedItem.id],
          blockInstance: {
            map: {
              ...previewLocation,
              w: itemW,
              h: itemH,
              rotation: 0,
            },
          },
          dx,
          dy,
          imageScale,
          xScale,
          yScale,
          transform,
          screen,
        })
      } else if (selectedItem.device) {
        Device({
          device: {
            map: {
              x: previewLocation.x,
              y: previewLocation.y,
              w: itemW,
            },
          },
          maxScale,
          dx,
          dy,
          rScale,
          xScale,
          yScale,
          transform,
          context,
          screen,
          fontSize,
          preview: true,
        })
      } else if (selectedItem.icon) {
        Pin({
          pin: {
            map: {
              x: previewLocation.x,
              y: previewLocation.y,
              w: itemW,
            },
            globalMapObject: selectedItem,
          },
          context,
          dx,
          dy,
          maxScale,
          rScale,
          xScale,
          yScale,
          transform,
          screen,
          fontSize,
          preview: true,
          force,
        })
      } else if (seatable(selectedItem)) {
        Seatable({
          space: {
            x: previewLocation.x,
            y: previewLocation.y,
            w: itemW,
            code: nextSpaceCode,
            spaceType: selectedItem,
            spaceAssignment: {},
          },
          context,
          dx,
          dy,
          maxScale,
          xScale,
          yScale,
          rScale,
          transform,
          screen,
          fontSize,
          canDrag: false,
          preview: true,
          force,
          isDirectBookingFlow,
        })
      } else {
        MeetingRoom({
          space: {
            x: previewLocation.x,
            y: previewLocation.y,
            w: itemW,
            name: '',
            code: nextSpaceCode,
            spaceType: selectedItem,
          },
          context,
          dx,
          dy,
          minScale,
          maxScale,
          xScale,
          yScale,
          rScale,
          transform,
          screen,
          fontSize,
          preview: true,
          force,
        })
      }
    }
  }
}

export default Map
